// Fire-and-forget client-side event tracking. Uses navigator.sendBeacon
// when available so an event queued right before navigation (share click,
// external redirect) still ships. Falls back to a plain fetch keepalive
// for browsers without sendBeacon or when it refuses the payload.
//
// Failures are swallowed — analytics must never surface as a UI error.
// Names are validated server-side against a whitelist (see api/routes/
// analytics-track.ts), so a typo here becomes a silent 200/false, not a
// crash.

export type AnalyticsEventName =
  | "qr_scanned"
  | "activation_started"
  | "reel_opened"
  | "reel_download_clicked"
  | "share_opened"
  | "share_clicked"
  | "share_copied"
  | "review_started"
  | "passport_photo_pick";

interface TrackArgs {
  name: AnalyticsEventName;
  sessionId?: string;
  passportId?: string;
  props?: Record<string, string | number | boolean>;
}

export function track(args: TrackArgs): void {
  const payload: Record<string, unknown> = { name: args.name };
  if (args.sessionId) payload.sessionId = args.sessionId;
  if (args.passportId) payload.passportId = args.passportId;
  if (args.props && Object.keys(args.props).length > 0) {
    payload.props = args.props;
  }
  const body = JSON.stringify(payload);
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon("/api/analytics/track", blob)) return;
    }
    void fetch("/api/analytics/track", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Absolutely nothing the UI can do.
  }
}
